import { useState, type FormEvent, type ReactNode } from 'react';
import { isValidEmail } from '../utils/email';
import mailIcon from './assets/mail.svg';

const PASSWORD_RESET_URL = 'https://www.ondemandorthodontist.com/wp-login.php?action=lostpassword';
const INVALID_INPUT = 'border-red-300 focus:ring-red-500 focus:border-red-500';
const VALID_INPUT = 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500';

interface LoginFormProps {
    logoSrc: string;
    title: string;
    subtitle?: string;
    alert: ReactNode;
    credentialsInvalid: boolean;
    isLoading: boolean;
    showPasswordReset: boolean;
    onCredentialsChange: () => void;
    onSubmit: (email: string, password: string) => void;
}

function SigningInSpinner() {
    return (
        <>
            <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                role="status"
                aria-label="Signing in"
            >
                <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                />
                <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
            </svg>
            <span className="sr-only">Signing in</span>
        </>
    );
}

function PasswordResetLink() {
    return (
        <div className="pt-4 border-t border-gray-200">
            <div className="text-center">
                <span className="text-sm text-gray-600">Forgot your password? </span>
                <a
                    href={PASSWORD_RESET_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 font-semibold transition-colors duration-200"
                >
                    Reset it here
                </a>
            </div>
        </div>
    );
}

interface CredentialFieldProps {
    invalid: boolean;
    value: string;
    onChange: (value: string) => void;
}

function EmailField({ invalid, value, onChange }: CredentialFieldProps) {
    const malformed = value !== '' && !isValidEmail(value);
    return (
        <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
            </label>
            <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <img
                        src={mailIcon}
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                        alt=""
                    />
                </div>
                <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className={`appearance-none block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm placeholder-gray-350 focus:outline-none sm:text-sm transition-colors duration-200 ${
                        malformed || invalid ? INVALID_INPUT : VALID_INPUT
                    }`}
                    placeholder="you@email.com"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                />
            </div>
            {malformed && (
                <p className="text-red-500 text-sm mt-1">Please enter a valid email address</p>
            )}
        </div>
    );
}

function PasswordField({ invalid, value, onChange }: CredentialFieldProps) {
    return (
        <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
            </label>
            <div className="mt-1">
                <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-350 focus:outline-none sm:text-sm transition-colors duration-200 ${
                        invalid ? INVALID_INPUT : VALID_INPUT
                    }`}
                    placeholder="Password"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                />
            </div>
        </div>
    );
}

export function LoginForm({
    logoSrc,
    title,
    subtitle,
    alert,
    credentialsInvalid,
    isLoading,
    showPasswordReset,
    onCredentialsChange,
    onSubmit
}: LoginFormProps) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const submitDisabled = isLoading || !isValidEmail(email) || !password;

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        onSubmit(email, password);
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="absolute top-10">
                <img src={logoSrc} alt="logo" className="h-10 w-auto" />
            </div>

            <div className="w-full max-w-xl">
                <div className="bg-white py-8 px-4 shadow-lg rounded-xl sm:px-10">
                    <div className="mb-8 text-center">
                        <h2 className="text-3xl font-bold text-gray-900">{title}</h2>
                        {subtitle && <p className="mt-2 text-sm text-gray-500">{subtitle}</p>}
                    </div>
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        {alert}

                        <EmailField
                            invalid={credentialsInvalid}
                            value={email}
                            onChange={(value) => {
                                setEmail(value);
                                onCredentialsChange();
                            }}
                        />

                        <PasswordField
                            invalid={credentialsInvalid}
                            value={password}
                            onChange={(value) => {
                                setPassword(value);
                                onCredentialsChange();
                            }}
                        />

                        <div>
                            <button
                                disabled={submitDisabled}
                                type="submit"
                                className={`w-full flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                                    submitDisabled
                                        ? 'bg-gray-300 cursor-not-allowed'
                                        : 'bg-gray-800 hover:bg-gray-900'
                                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900`}
                            >
                                {isLoading ? <SigningInSpinner /> : 'Sign in'}
                            </button>
                        </div>

                        {showPasswordReset && <PasswordResetLink />}
                    </form>
                </div>
            </div>
        </div>
    );
}
